import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../icons';

// Sostituisce <input type="datetime-local">: il calendario a comparsa del
// browser non è personalizzabile via CSS in modo uniforme tra i motori (e'
// UI di sistema, non DOM), quindi resta sempre incoerente col resto
// dell'app qualunque stile si applichi al campo. Qui sia il pop-up (griglia
// giorni) sia l'ora (due campi ore/minuti) sono disegnati da zero con lo
// stesso trattamento "card elevata" di Select/Combobox — nessun controllo
// nativo del browser rimasto, a differenza della prima versione che per
// l'ora usava ancora <input type="time">.
//
// value/onChange restano identici all'input nativo che sostituisce: stessa
// stringa locale "AAAA-MM-GGTHH:mm", stesso event.target.value nel callback
// — chi lo usa non deve cambiare nient'altro.

const STYLE_ID = 'dtp-styles';
const CSS = `
.dtp-trigger {
  display: inline-flex; align-items: center; gap: 0.5rem; cursor: pointer;
  color: var(--text-primary); text-align: left; font-size: 0.88rem;
}
.dtp-trigger.is-empty { color: var(--text-muted); }
.dtp-trigger .icon { flex-shrink: 0; color: var(--text-muted); }
.dtp-pop {
  position: fixed; z-index: 70; width: 272px; padding: 0.75rem;
  background: var(--bg-elevated); border: 1px solid var(--border-default);
  border-radius: var(--radius-sm); box-shadow: var(--shadow-lg);
  animation: combobox-in 0.12s ease;
}
.dtp-nav { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.6rem; }
.dtp-nav-btn {
  display: grid; place-items: center; width: 26px; height: 26px; padding: 0;
  border: none; border-radius: 7px; background: transparent; color: var(--text-secondary); cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.dtp-nav-btn:hover { background: var(--glass-brand); color: var(--text-primary); }
.dtp-nav-label { font-size: 0.86rem; font-weight: 600; color: var(--text-primary); text-transform: capitalize; }
.dtp-weekdays, .dtp-days { display: grid; grid-template-columns: repeat(7, 1fr); }
.dtp-weekdays { margin-bottom: 0.2rem; }
.dtp-weekday { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; color: var(--text-muted); text-align: center; padding: 0.2rem 0; }
.dtp-day {
  display: grid; place-items: center; width: 100%; aspect-ratio: 1; margin: 1px 0;
  border: none; border-radius: 7px; background: transparent; color: var(--text-primary);
  font-size: 0.82rem; font-family: inherit; cursor: pointer; transition: background 0.12s ease, color 0.12s ease;
}
.dtp-day:hover { background: var(--glass-brand); }
.dtp-day.is-outside { color: var(--text-muted); opacity: 0.5; }
.dtp-day.is-today { box-shadow: inset 0 0 0 1px var(--hiplaas-orange); }
.dtp-day.is-selected { background: var(--gradient-accent); color: #fff; font-weight: 600; }
.dtp-day.is-selected.is-today { box-shadow: none; }
.dtp-foot { display: flex; align-items: center; gap: 0.5rem; margin-top: 0.65rem; padding-top: 0.65rem; border-top: 1px solid var(--border-subtle); }
.dtp-time-row {
  display: flex; align-items: center; gap: 0.3rem; flex: 1; padding: 0.4rem 0.55rem;
  background: var(--bg-base); border: 1px solid var(--border-default); border-radius: 7px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.dtp-time-row:focus-within { border-color: var(--hiplaas-blue-bright); box-shadow: 0 0 0 3px var(--glow-brand); }
.dtp-time-row .icon { color: var(--text-muted); flex-shrink: 0; }
.dtp-time-field {
  width: 1.4rem; border: none; background: transparent; padding: 0;
  color: var(--text-primary); font-size: 0.82rem; font-family: inherit;
  font-variant-numeric: tabular-nums; text-align: center;
}
.dtp-time-field:focus { outline: none; }
.dtp-time-sep { color: var(--text-muted); font-weight: 600; }
.dtp-clear {
  padding: 0.4rem 0.6rem; border: none; border-radius: 7px; background: transparent;
  color: var(--text-muted); font-size: 0.78rem; font-family: inherit; cursor: pointer; transition: background 0.12s ease, color 0.12s ease;
}
.dtp-clear:hover { background: var(--err-bg); color: var(--err); }
`;

function ensureStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const MONTHS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const pad = (n) => String(n).padStart(2, '0');

// Parsing manuale (niente `new Date(stringaISO)`): un motore JS puo' leggere
// "AAAA-MM-GG" come UTC mezzanotte e restituire il giorno prima/dopo una
// volta convertito in ora locale. Split diretto = zero ambiguita' di fuso.
function parseValue(value) {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = (datePart || '').split('-').map(Number);
  const [h, mi] = (timePart || '00:00').split(':').map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d, h: h || 0, mi: mi || 0 };
}

function formatValue({ y, m, d, h, mi }) {
  return `${y}-${pad(m)}-${pad(d)}T${pad(h)}:${pad(mi)}`;
}

// Campo ore/minuti: buffer di testo locale (permette di digitare liberamente,
// es. cancellare e scrivere "9" senza che venga subito riformattato a "09")
// che si propaga al valore vero non appena le 2 cifre sono complete, e si
// normalizza (pad/clamp) alla perdita del focus — stesso comportamento del
// vecchio <input type="time"> nativo, che committeva solo a orario completo.
function TimeField({ label, value, max, onCommit }) {
  const [text, setText] = useState(pad(value));
  useEffect(() => { setText(pad(value)); }, [value]);
  return (
    <input
      type="text"
      inputMode="numeric"
      className="dtp-time-field"
      aria-label={label}
      value={text}
      onFocus={(e) => e.target.select()}
      onChange={(e) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
        setText(raw);
        if (raw.length === 2) onCommit(Math.min(max, parseInt(raw, 10)));
      }}
      onBlur={() => {
        const n = text === '' ? 0 : Math.min(max, parseInt(text, 10) || 0);
        setText(pad(n));
        onCommit(n);
      }}
    />
  );
}

function DateTimePicker({ id, value, onChange, ariaLabel, className, placeholder = 'gg/mm/aaaa, --:--' }) {
  useEffect(ensureStyles, []);
  const parsed = parseValue(value);
  const today = new Date();

  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [viewY, setViewY] = useState((parsed || { y: today.getFullYear() }).y);
  const [viewM, setViewM] = useState((parsed || { m: today.getMonth() + 1 }).m);
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  const commit = useCallback((next) => {
    onChange({ target: { value: next ? formatValue(next) : '' } });
  }, [onChange]);

  const openPicker = useCallback(() => {
    const p = parseValue(value);
    setViewY((p || { y: today.getFullYear() }).y);
    setViewM((p || { m: today.getMonth() + 1 }).m);
    setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom + 6, left: r.left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const changeMonth = (delta) => {
    let y = viewY, m = viewM + delta;
    if (m < 1) { m = 12; y -= 1; }
    else if (m > 12) { m = 1; y += 1; }
    setViewY(y); setViewM(m);
  };

  const pickDay = (y, m, d) => {
    commit({ y, m, d, h: parsed?.h ?? 0, mi: parsed?.mi ?? 0 });
  };

  const commitTime = (patch) => {
    const base = parsed || { y: viewY, m: viewM, d: today.getDate(), h: 0, mi: 0 };
    commit({ ...base, ...patch });
  };

  // Griglia 6x7 fissa (sempre la stessa altezza, mese corto o lungo):
  // giorni del mese precedente/successivo per riempire le celle di contorno.
  const firstOfMonth = new Date(viewY, viewM - 1, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7; // lunedi'=0
  const daysInMonth = new Date(viewY, viewM, 0).getDate();
  const daysInPrevMonth = new Date(viewY, viewM - 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) {
    const d = daysInPrevMonth - startOffset + 1 + i;
    const m = viewM === 1 ? 12 : viewM - 1;
    const y = viewM === 1 ? viewY - 1 : viewY;
    cells.push({ y, m, d, outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++) cells.push({ y: viewY, m: viewM, d, outside: false });
  const nextMonth = viewM === 12 ? 1 : viewM + 1;
  const nextYear = viewM === 12 ? viewY + 1 : viewY;
  for (let d = 1; cells.length < 42; d++) cells.push({ y: nextYear, m: nextMonth, d, outside: true });

  const displayText = parsed
    ? `${pad(parsed.d)}/${pad(parsed.m)}/${parsed.y}, ${pad(parsed.h)}:${pad(parsed.mi)}`
    : placeholder;

  return (
    <>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`input dtp-trigger${parsed ? '' : ' is-empty'}${className ? ` ${className}` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => (open ? setOpen(false) : openPicker())}
      >
        <Icon name="calendar" size={15} />
        <span>{displayText}</span>
      </button>
      {open && rect && createPortal(
        <div ref={popRef} className="dtp-pop" role="dialog" aria-label={ariaLabel || 'Seleziona data e ora'} style={{ top: rect.top, left: rect.left }}>
          <div className="dtp-nav">
            <button type="button" className="dtp-nav-btn" onClick={() => changeMonth(-1)} aria-label="Mese precedente">
              <Icon name="chevronUp" size={14} style={{ transform: 'rotate(-90deg)' }} />
            </button>
            <span className="dtp-nav-label">{MONTHS[viewM - 1]} {viewY}</span>
            <button type="button" className="dtp-nav-btn" onClick={() => changeMonth(1)} aria-label="Mese successivo">
              <Icon name="chevronUp" size={14} style={{ transform: 'rotate(90deg)' }} />
            </button>
          </div>
          <div className="dtp-weekdays">
            {WEEKDAYS.map((w) => <span key={w} className="dtp-weekday">{w}</span>)}
          </div>
          <div className="dtp-days">
            {cells.map((c, i) => {
              const isToday = c.y === today.getFullYear() && c.m === today.getMonth() + 1 && c.d === today.getDate();
              const isSelected = parsed && c.y === parsed.y && c.m === parsed.m && c.d === parsed.d;
              return (
                <button
                  type="button"
                  key={i}
                  className={`dtp-day${c.outside ? ' is-outside' : ''}${isToday ? ' is-today' : ''}${isSelected ? ' is-selected' : ''}`}
                  onClick={() => pickDay(c.y, c.m, c.d)}
                >
                  {c.d}
                </button>
              );
            })}
          </div>
          <div className="dtp-foot">
            <div className="dtp-time-row">
              <Icon name="clock" size={14} />
              <TimeField label="Ora" value={parsed?.h ?? 0} max={23} onCommit={(h) => commitTime({ h })} />
              <span className="dtp-time-sep">:</span>
              <TimeField label="Minuti" value={parsed?.mi ?? 0} max={59} onCommit={(mi) => commitTime({ mi })} />
            </div>
            {parsed && (
              <button type="button" className="dtp-clear" onClick={() => commit(null)}>Cancella</button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default DateTimePicker;
